// ============================================================================
// SignaturePad — canvas signature capture with mouse + touch support.
// ============================================================================
// Exposes via ref: getDataUrl() (returns image/png base64 data URL) and
// clear(). Parent decides when to read the value (e.g. on form submit).
// ============================================================================

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';

export interface SignaturePadHandle {
  getDataUrl: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

interface Props {
  height?: number; // px; width fills parent
  disabled?: boolean;
}

export const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad(
  { height = 180, disabled },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number } | null>(null);
  const [empty, setEmpty] = useState(true);

  // Resize canvas to fit parent width, with DPR awareness for crisp lines.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const fit = () => {
      const rect = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      c.width = Math.floor(rect.width * dpr);
      c.height = Math.floor(height * dpr);
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1f2937';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, height);
      ctxRef.current = ctx;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(c.parentElement || c);
    return () => ro.disconnect();
  }, [height]);

  useImperativeHandle(ref, () => ({
    getDataUrl: () => {
      const c = canvasRef.current;
      if (!c || empty) return null;
      return c.toDataURL('image/png');
    },
    clear: () => {
      const c = canvasRef.current;
      const ctx = ctxRef.current;
      if (!c || !ctx) return;
      const rect = c.getBoundingClientRect();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, height);
      setEmpty(true);
    },
    isEmpty: () => empty,
  }));

  const eventPoint = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
    const c = canvasRef.current;
    if (!c) return null;
    const rect = c.getBoundingClientRect();
    if ('touches' in e) {
      const t = e.touches[0] || e.changedTouches[0];
      if (!t) return null;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    const p = eventPoint(e);
    if (!p) return;
    drawingRef.current = true;
    lastRef.current = p;
  };

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const last = lastRef.current;
    const p = eventPoint(e);
    if (!ctx || !last || !p) return;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (empty) setEmpty(false);
  };

  const end = () => {
    drawingRef.current = false;
    lastRef.current = null;
  };

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        style={{ height, touchAction: 'none' }}
        className="w-full bg-white border border-gray-300 rounded-md"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {empty && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400">
          Sign here
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          const c = canvasRef.current;
          const ctx = ctxRef.current;
          if (!c || !ctx) return;
          const rect = c.getBoundingClientRect();
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, rect.width, height);
          setEmpty(true);
        }}
        disabled={disabled || empty}
        className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        <Eraser className="h-3 w-3" /> Clear
      </button>
    </div>
  );
});
